// src/pages/RolesPermissions.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ShieldCheck,
  Shield,
  Plus,
  Copy,
  Edit2,
  Trash2,
  Lock,
  Save,
  Users,
  AlertCircle,
  Eye,
  EyeOff,
  Pencil,
  Sparkles,
  Package,
  Receipt,
  Building2,
  History,
  Settings as SettingsIcon,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Info,
  UserPlus,
  UserCheck,
  Search,
} from 'lucide-react';
import { Role, Permission, User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiRequest } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { Badge } from '../components/ui/Badge';
import { TableSkeleton } from '../components/ui/TableSkeleton';

// ---------------------------------------------------------------------------
// Business Configuration Definitions
// ---------------------------------------------------------------------------

interface PriceTierConfig {
  id: string;
  title: string;
  shortName: string;
  description: string;
  isConfidential: boolean;
  viewKey: string;
  editKey: string;
}

const PRICE_TIERS: PriceTierConfig[] = [
  {
    id: 'cost',
    title: 'Factory Base Cost & Net Cost',
    shortName: 'Base Cost & Net',
    description: 'Internal manufacturing cost and calculated net cost markup percentage.',
    isConfidential: true,
    viewKey: 'pricing.view_cost',
    editKey: 'pricing.edit_cost',
  },
  {
    id: 'traders',
    title: 'Wholesale Traders Tier',
    shortName: 'Wholesale Traders',
    description: 'Bulk distributor and wholesale trading partner pricing schedule.',
    isConfidential: false,
    viewKey: 'pricing.view_traders',
    editKey: 'pricing.edit_traders',
  },
  {
    id: 'sub_cont',
    title: 'Sub-Contractor Tier',
    shortName: 'Sub-Contractor',
    description: 'Tiered pricing schedule for construction project sub-contractors.',
    isConfidential: false,
    viewKey: 'pricing.view_sub_cont',
    editKey: 'pricing.edit_sub_cont',
  },
  {
    id: 'main_cont',
    title: 'Main Contractor Tier',
    shortName: 'Main Contractor',
    description: 'Pricing schedule for main construction contractors & major tenders.',
    isConfidential: false,
    viewKey: 'pricing.view_main_cont',
    editKey: 'pricing.edit_main_cont',
  },
  {
    id: 'client',
    title: 'Client Direct Retail Tier',
    shortName: 'Client Retail',
    description: 'End customer, walk-in buyer, and direct retail quote pricing schedule.',
    isConfidential: false,
    viewKey: 'pricing.view_client',
    editKey: 'pricing.edit_client',
  },
];

interface ModuleDefinition {
  id: string;
  name: string;
  description: string;
  icon: any;
  viewKey: string;
  keys: string[];
  actions: {
    key: string;
    label: string;
    desc: string;
  }[];
}

const MODULE_DEFS: ModuleDefinition[] = [
  {
    id: 'products',
    name: 'Product Catalog',
    description: 'SKUs, manufacturing formulations, units, categories, and catalog specifications.',
    icon: Package,
    viewKey: 'products.view',
    keys: ['products.view', 'products.create', 'products.edit', 'products.delete'],
    actions: [
      { key: 'products.view', label: 'View Catalog', desc: 'Browse products, search SKUs, view specs and categories' },
      { key: 'products.create', label: 'Add Products', desc: 'Create new products and add formulations to catalog' },
      { key: 'products.edit', label: 'Edit Products', desc: 'Modify product names, unit specifications, and categories' },
      { key: 'products.delete', label: 'Archive Products', desc: 'Archive or remove products from company catalog' },
    ],
  },
  {
    id: 'data',
    name: 'Invoices & Sales Records',
    description: 'Departmental billing entries, sales orders, invoices, and profitability data.',
    icon: Receipt,
    viewKey: 'data.view',
    keys: ['data.view', 'data.create', 'data.edit', 'data.delete', 'data.view_all_departments'],
    actions: [
      { key: 'data.view', label: 'View Invoices', desc: 'View departmental sales invoices and transaction records' },
      { key: 'data.create', label: 'Create Records', desc: 'Generate new invoices and sales records' },
      { key: 'data.edit', label: 'Edit Records', desc: 'Modify invoice amounts, dates, and item lines' },
      { key: 'data.delete', label: 'Delete Records', desc: 'Remove invoice and sales entries' },
      { key: 'data.view_all_departments', label: 'Company-Wide Access', desc: 'View records across all departments (bypasses isolation)' },
    ],
  },
  {
    id: 'departments',
    name: 'Company Departments',
    description: 'Organizational divisions, cost centers, and manager allocations.',
    icon: Building2,
    viewKey: 'departments.view',
    keys: ['departments.view', 'departments.create', 'departments.edit', 'departments.delete'],
    actions: [
      { key: 'departments.view', label: 'View Departments', desc: 'View department structure and personnel count' },
      { key: 'departments.create', label: 'Create Departments', desc: 'Set up new company departments and cost centers' },
      { key: 'departments.edit', label: 'Edit Departments', desc: 'Reassign managers and update department details' },
      { key: 'departments.delete', label: 'Deactivate Departments', desc: 'Decommission company departments' },
    ],
  },
  {
    id: 'users',
    name: 'User Management',
    description: 'Employee logins, security credentials, and department assignments.',
    icon: Users,
    viewKey: 'users.view',
    keys: ['users.view', 'users.create', 'users.edit', 'users.delete'],
    actions: [
      { key: 'users.view', label: 'View Directory', desc: 'Browse employee list and account statuses' },
      { key: 'users.create', label: 'Provision Users', desc: 'Create new team accounts and issue temporary passwords' },
      { key: 'users.edit', label: 'Edit Accounts', desc: 'Update profile information, departments, and roles' },
      { key: 'users.delete', label: 'Deactivate Users', desc: 'Suspend or remove employee accounts' },
    ],
  },
  {
    id: 'roles',
    name: 'Roles & Security Privileges',
    description: 'Organizational role hierarchy and fine-grained permission control.',
    icon: Shield,
    viewKey: 'roles.view',
    keys: ['roles.view', 'roles.create', 'roles.edit', 'roles.delete'],
    actions: [
      { key: 'roles.view', label: 'View Roles', desc: 'Inspect available roles and their privileges' },
      { key: 'roles.create', label: 'Create & Clone Roles', desc: 'Define new custom roles and duplicate existing ones' },
      { key: 'roles.edit', label: 'Edit Permissions', desc: 'Modify role permission matrix and security levels' },
      { key: 'roles.delete', label: 'Delete Custom Roles', desc: 'Remove unused custom organizational roles' },
    ],
  },
  {
    id: 'audit',
    name: 'Security Audit Trail',
    description: 'Immutable timeline of user actions, price adjustments, and system events.',
    icon: History,
    viewKey: 'audit.view',
    keys: ['audit.view'],
    actions: [
      { key: 'audit.view', label: 'View Audit Logs', desc: 'Inspect tamper-proof user actions and system events' },
    ],
  },
  {
    id: 'settings',
    name: 'System Settings & Bot Alerts',
    description: 'Default category markup rules, Telegram bot alerts, and system parameters.',
    icon: SettingsIcon,
    viewKey: 'settings.view',
    keys: ['settings.view', 'settings.edit'],
    actions: [
      { key: 'settings.view', label: 'View Configuration', desc: 'Review pricing formula rules and Telegram bot status' },
      { key: 'settings.edit', label: 'Modify Settings', desc: 'Update markup percentages, bot token, and chat ID' },
    ],
  },
];

interface BusinessPreset {
  id: string;
  name: string;
  badge: string;
  badgeColor: string;
  description: string;
  keys: string[];
}

const BUSINESS_PRESETS: BusinessPreset[] = [
  {
    id: 'sales',
    name: 'Sales & Estimation Team',
    badge: 'Front-Line Sales',
    badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    description: 'Quotes & tenders for clients, wholesale traders, and contractors. Factory base costs are strictly hidden.',
    keys: [
      'products.view',
      'data.view',
      'pricing.view_traders',
      'pricing.view_sub_cont',
      'pricing.view_main_cont',
      'pricing.view_client',
    ],
  },
  {
    id: 'purchase',
    name: 'Purchase & Procurement Team',
    badge: 'Cost Operations',
    badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    description: 'Manages factory base costs, supplier pricing, and catalog formulations. Can edit base costs.',
    keys: [
      'products.view',
      'products.create',
      'products.edit',
      'data.view',
      'data.create',
      'data.edit',
      'pricing.view_cost',
      'pricing.edit_cost',
      'pricing.view_traders',
      'pricing.view_sub_cont',
      'pricing.view_main_cont',
      'pricing.view_client',
    ],
  },
  {
    id: 'finance',
    name: 'Finance & Executive Auditor',
    badge: 'Auditing & P&L',
    badgeColor: 'bg-amber-50 text-amber-700 border-amber-200',
    description: 'Audits company-wide sales profitability, margins, and invoice records. Read-only pricing access.',
    keys: [
      'products.view',
      'data.view',
      'data.view_all_departments',
      'audit.view',
      'pricing.view_cost',
      'pricing.view_traders',
      'pricing.view_sub_cont',
      'pricing.view_main_cont',
      'pricing.view_client',
    ],
  },
  {
    id: 'plant',
    name: 'Plant / Factory Operations',
    badge: 'Factory Floor',
    badgeColor: 'bg-purple-50 text-purple-700 border-purple-200',
    description: 'Manufacturing formulations and batch logs. Commercial pricing tiers are completely hidden.',
    keys: [
      'products.view',
      'data.view',
      'data.create',
    ],
  },
  {
    id: 'manager',
    name: 'Department Manager',
    badge: 'Department Lead',
    badgeColor: 'bg-blue-50 text-blue-700 border-blue-200',
    description: 'Full management over departmental invoices, team visibility, and standard selling prices.',
    keys: [
      'products.view',
      'products.create',
      'products.edit',
      'data.view',
      'data.create',
      'data.edit',
      'data.delete',
      'departments.view',
      'users.view',
      'pricing.view_cost',
      'pricing.view_traders',
      'pricing.edit_traders',
      'pricing.view_sub_cont',
      'pricing.edit_sub_cont',
      'pricing.view_main_cont',
      'pricing.edit_main_cont',
      'pricing.view_client',
      'pricing.edit_client',
    ],
  },
  {
    id: 'viewer',
    name: 'Read-Only Viewer',
    badge: 'Restricted',
    badgeColor: 'bg-slate-100 text-slate-700 border-slate-200',
    description: 'Basic view privileges for guest or non-commercial team members.',
    keys: [
      'products.view',
      'data.view',
      'pricing.view_client',
    ],
  },
];

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const RolesPermissions: React.FC = () => {
  const { can, refreshUser } = useAuth();
  const { success, error: toastError } = useToast();

  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [selectedPermIds, setSelectedPermIds] = useState<Set<number>>(new Set());
  const [initialPermIds, setInitialPermIds] = useState<Set<number>>(new Set());

  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [savingPerms, setSavingPerms] = useState(false);

  // Active view tab: 'pricing' | 'modules' | 'members'
  const [activeTab, setActiveTab] = useState<'pricing' | 'modules' | 'members'>('pricing');

  // Expanded state for fine-grained module details
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});

  // Preset picker modal state
  const [presetModalOpen, setPresetModalOpen] = useState(false);

  // Role Create / Edit / Clone Modal
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [cloneFromId, setCloneFromId] = useState<string>('');
  const [roleName, setRoleName] = useState('');
  const [roleDesc, setRoleDesc] = useState('');
  const [rolePresetChoice, setRolePresetChoice] = useState<string>('custom');
  const [roleLoading, setRoleLoading] = useState(false);

  // Reassign User to Role Modal
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedUserToAssign, setSelectedUserToAssign] = useState<string>('');
  const [assigningUser, setAssigningUser] = useState(false);

  // Delete Role Dialog
  const [confirmDelete, setConfirmDelete] = useState<{ open: boolean; role: Role | null; loading: boolean }>({
    open: false,
    role: null,
    loading: false,
  });

  // Search filter for roles
  const [roleSearch, setRoleSearch] = useState('');

  // -------------------------------------------------------------------------
  // Data Loading
  // -------------------------------------------------------------------------

  const loadRoles = useCallback(async (selectId?: number) => {
    try {
      setLoadingRoles(true);
      const data = await apiRequest<Role[]>('/api/roles');
      setRoles(data || []);
      if (data && data.length > 0) {
        setSelectedRole(prev => {
          if (selectId) return data.find(r => r.id === selectId) || data[0];
          if (!prev) return data[0];
          return data.find(r => r.id === prev.id) || data[0];
        });
      }
    } catch (err: any) {
      toastError(err.message || 'Failed to load roles.');
    } finally {
      setLoadingRoles(false);
    }
  }, [toastError]);

  const loadAllUsers = useCallback(async () => {
    try {
      const res = await apiRequest<{ items: User[] }>('/api/users?pageSize=100');
      if (res?.items) {
        setAllUsers(res.items);
      }
    } catch {
      // Ignored if user has no permission
    }
  }, []);

  useEffect(() => {
    loadRoles();
    loadAllUsers();
  }, [loadRoles, loadAllUsers]);

  // Load permissions for selected role only when selectedRoleId changes
  const selectedRoleId = selectedRole?.id;

  useEffect(() => {
    if (!selectedRoleId) return;
    let isCancelled = false;

    const loadRolePermissions = async () => {
      try {
        setLoadingPerms(true);
        const res = await apiRequest<{ role: Role; permissions: Permission[] }>(
          `/api/roles/${selectedRoleId}/permissions`
        );
        if (isCancelled) return;
        setPermissions(res.permissions || []);
        const allowedIds = new Set<number>();
        (res.permissions || []).forEach(p => {
          if (p.allowed) allowedIds.add(p.id);
        });
        setSelectedPermIds(allowedIds);
        setInitialPermIds(new Set(allowedIds));
      } catch (err: any) {
        if (!isCancelled) {
          toastError(err.message || 'Failed to load permissions matrix.');
        }
      } finally {
        if (!isCancelled) {
          setLoadingPerms(false);
        }
      }
    };

    loadRolePermissions();

    return () => {
      isCancelled = true;
    };
  }, [selectedRoleId, toastError]);

  // Map permission keys to objects for instant lookup
  const permByKey = useMemo(() => {
    const map: Record<string, Permission> = {};
    permissions.forEach(p => {
      map[p.key] = p;
    });
    return map;
  }, [permissions]);

  const isSuperAdminRole = Boolean(selectedRole?.is_system_role);

  // Check if permissions have unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (isSuperAdminRole) return false;
    if (selectedPermIds.size !== initialPermIds.size) return true;
    for (const id of selectedPermIds) {
      if (!initialPermIds.has(id)) return true;
    }
    return false;
  }, [selectedPermIds, initialPermIds, isSuperAdminRole]);

  // Users belonging to selected role
  const roleMembers = useMemo(() => {
    if (!selectedRole) return [];
    return allUsers.filter(u => u.role_id === selectedRole.id);
  }, [allUsers, selectedRole]);

  // -------------------------------------------------------------------------
  // Handlers: Permissions Toggling & Presets
  // -------------------------------------------------------------------------

  const togglePermissionByKey = (key: string) => {
    if (isSuperAdminRole) return;
    const perm = permByKey[key];
    if (!perm) return;

    setSelectedPermIds(prev => {
      const next = new Set(prev);
      if (next.has(perm.id)) {
        next.delete(perm.id);
      } else {
        next.add(perm.id);
      }
      return next;
    });
  };

  const setPermissionByKey = (key: string, allow: boolean) => {
    if (isSuperAdminRole) return;
    const perm = permByKey[key];
    if (!perm) return;

    setSelectedPermIds(prev => {
      const next = new Set(prev);
      if (allow) {
        next.add(perm.id);
      } else {
        next.delete(perm.id);
      }
      return next;
    });
  };

  // Module level selector: 'none' | 'view' | 'full'
  const handleSetModuleLevel = (mod: ModuleDefinition, level: 'none' | 'view' | 'full') => {
    if (isSuperAdminRole) return;

    setSelectedPermIds(prev => {
      const next = new Set(prev);

      if (level === 'none') {
        // Remove all keys of this module
        mod.keys.forEach(k => {
          const p = permByKey[k];
          if (p) next.delete(p.id);
        });
      } else if (level === 'view') {
        // Add view key, remove write/edit keys
        mod.keys.forEach(k => {
          const p = permByKey[k];
          if (!p) return;
          if (k === mod.viewKey) {
            next.add(p.id);
          } else {
            next.delete(p.id);
          }
        });
      } else if (level === 'full') {
        // Add all keys of this module
        mod.keys.forEach(k => {
          const p = permByKey[k];
          if (p) next.add(p.id);
        });
      }

      return next;
    });
  };

  // Determine current access level for a module
  const getModuleAccessLevel = (mod: ModuleDefinition): 'none' | 'view' | 'full' | 'custom' => {
    if (isSuperAdminRole) return 'full';

    const allowedCount = mod.keys.filter(k => {
      const p = permByKey[k];
      return p && selectedPermIds.has(p.id);
    }).length;

    if (allowedCount === 0) return 'none';
    if (allowedCount === mod.keys.length) return 'full';

    const viewPerm = permByKey[mod.viewKey];
    if (allowedCount === 1 && viewPerm && selectedPermIds.has(viewPerm.id)) {
      return 'view';
    }

    return 'custom';
  };

  // Apply a business template preset to the selected role
  const handleApplyPreset = (preset: BusinessPreset) => {
    if (isSuperAdminRole) return;

    const newPermIds = new Set<number>();
    preset.keys.forEach(key => {
      const p = permByKey[key];
      if (p) newPermIds.add(p.id);
    });

    setSelectedPermIds(newPermIds);
    setPresetModalOpen(false);
    success(`Applied "${preset.name}" preset template. Click "Save Permissions" to commit changes.`);
  };

  // Save permissions to backend
  const handleSavePermissions = async () => {
    if (!selectedRole || isSuperAdminRole) return;
    setSavingPerms(true);
    try {
      await apiRequest(`/api/roles/${selectedRole.id}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({
          permissionIds: Array.from(selectedPermIds),
        }),
      });
      setInitialPermIds(new Set(selectedPermIds));
      success(`Permissions for role "${selectedRole.name}" updated and saved successfully!`);
      await refreshUser();
      loadRoles();
    } catch (err: any) {
      toastError(err.message || 'Failed to update permissions.');
    } finally {
      setSavingPerms(false);
    }
  };

  // -------------------------------------------------------------------------
  // Handlers: Role CRUD & Assignment
  // -------------------------------------------------------------------------

  const handleOpenCreate = () => {
    setEditingRole(null);
    setCloneFromId('');
    setRoleName('');
    setRoleDesc('');
    setRolePresetChoice('custom');
    setRoleModalOpen(true);
  };

  const handleOpenClone = (role: Role) => {
    setEditingRole(null);
    setCloneFromId(String(role.id));
    setRoleName(`${role.name} (Copy)`);
    setRoleDesc(`Cloned from ${role.name}`);
    setRolePresetChoice('custom');
    setRoleModalOpen(true);
  };

  const handleOpenEdit = (role: Role) => {
    setEditingRole(role);
    setCloneFromId('');
    setRoleName(role.name);
    setRoleDesc(role.description || '');
    setRolePresetChoice('custom');
    setRoleModalOpen(true);
  };

  const handleSaveRole = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roleName.trim()) {
      toastError('Role name is required.');
      return;
    }

    setRoleLoading(true);
    try {
      if (editingRole) {
        await apiRequest(`/api/roles/${editingRole.id}`, {
          method: 'PUT',
          body: JSON.stringify({ name: roleName.trim(), description: roleDesc.trim() }),
        });
        success(`Role "${roleName}" updated successfully.`);
      } else {
        const payload: any = { name: roleName.trim(), description: roleDesc.trim() };
        if (cloneFromId) payload.clone_from_role_id = Number(cloneFromId);

        const res = await apiRequest<{ id: number }>('/api/roles', {
          method: 'POST',
          body: JSON.stringify(payload),
        });

        // If a business preset was chosen, automatically assign those permissions
        if (rolePresetChoice !== 'custom' && res.id) {
          const preset = BUSINESS_PRESETS.find(p => p.id === rolePresetChoice);
          if (preset) {
            // Find permission IDs for this preset
            const permIdsToSave: number[] = [];
            preset.keys.forEach(k => {
              const p = permByKey[k];
              if (p) permIdsToSave.push(p.id);
            });

            if (permIdsToSave.length > 0) {
              await apiRequest(`/api/roles/${res.id}/permissions`, {
                method: 'PUT',
                body: JSON.stringify({ permissionIds: permIdsToSave }),
              });
            }
          }
        }

        success(`Role "${roleName}" created successfully.`);
        // Select new role
        const newRole: Role = {
          id: res.id,
          name: roleName.trim(),
          description: roleDesc.trim(),
          is_system_role: false,
          status: 'active',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          user_count: 0,
        };
        setSelectedRole(newRole);
      }
      setRoleModalOpen(false);
      loadRoles();
    } catch (err: any) {
      toastError(err.message || 'Failed to save role.');
    } finally {
      setRoleLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!confirmDelete.role) return;
    setConfirmDelete(prev => ({ ...prev, loading: true }));
    try {
      await apiRequest(`/api/roles/${confirmDelete.role.id}`, { method: 'DELETE' });
      success(`Role "${confirmDelete.role.name}" removed.`);
      setConfirmDelete({ open: false, role: null, loading: false });
      setSelectedRole(null);
      loadRoles();
    } catch (err: any) {
      toastError(err.message || 'Failed to delete role.');
      setConfirmDelete(prev => ({ ...prev, loading: false }));
    }
  };

  // Reassign user to current role
  const handleAssignUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRole || !selectedUserToAssign) return;

    setAssigningUser(true);
    try {
      await apiRequest(`/api/users/${selectedUserToAssign}`, {
        method: 'PUT',
        body: JSON.stringify({ role_id: selectedRole.id }),
      });
      success('User assigned to role successfully.');
      setAssignModalOpen(false);
      setSelectedUserToAssign('');
      loadAllUsers();
      loadRoles();
    } catch (err: any) {
      toastError(err.message || 'Failed to assign user to role.');
    } finally {
      setAssigningUser(false);
    }
  };

  // Filtered roles list
  const filteredRoles = useMemo(() => {
    if (!roleSearch.trim()) return roles;
    const q = roleSearch.toLowerCase();
    return roles.filter(
      r => r.name.toLowerCase().includes(q) || (r.description && r.description.toLowerCase().includes(q))
    );
  }, [roles, roleSearch]);

  // Sensitive cost visibility status for currently selected role
  const isCostViewAllowed = useMemo(() => {
    if (isSuperAdminRole) return true;
    const costPerm = permByKey['pricing.view_cost'];
    return Boolean(costPerm && selectedPermIds.has(costPerm.id));
  }, [isSuperAdminRole, permByKey, selectedPermIds]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <span>Roles & Security Privileges</span>
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
              <ShieldCheck className="w-3.5 h-3.5 text-indigo-600" />
              Role-Based Access Control
            </span>
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Configure price confidentiality, module access levels, and organizational staff permissions with clarity.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {can('roles.create') && (
            <Button onClick={handleOpenCreate} icon={<Plus className="w-4 h-4" />}>
              Create Role
            </Button>
          )}
        </div>
      </div>

      {/* Main Grid: Left Roles Roster, Right Permission Workspace */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Available Roles (4 Cols) */}
        <div className="lg:col-span-4 bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Available Roles</h3>
              <p className="text-[11px] text-slate-400">Select a role to inspect or update</p>
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
              {roles.length} total
            </span>
          </div>

          {/* Search Roles */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-3 text-slate-400" />
            <input
              type="text"
              placeholder="Filter roles..."
              value={roleSearch}
              onChange={e => setRoleSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:ring-1 focus:ring-indigo-500"
            />
          </div>

          {loadingRoles ? (
            <div className="space-y-2 py-4 animate-pulse">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-16 bg-slate-100 rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="space-y-2 max-h-[calc(100vh-320px)] overflow-y-auto pr-1">
              {filteredRoles.map(role => {
                const isSelected = selectedRole?.id === role.id;
                const isSystem = Boolean(role.is_system_role);

                return (
                  <div
                    key={role.id}
                    onClick={() => setSelectedRole(role)}
                    className={`p-3.5 rounded-xl border cursor-pointer transition-all ${
                      isSelected
                        ? 'border-indigo-600 bg-indigo-50/60 shadow-sm ring-1 ring-indigo-600'
                        : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {isSystem ? (
                          <div className="p-1.5 rounded-lg bg-amber-100 text-amber-700">
                            <Lock className="w-3.5 h-3.5" />
                          </div>
                        ) : (
                          <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                            <Shield className="w-3.5 h-3.5" />
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-bold text-slate-900">{role.name}</span>
                            {isSystem && (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.2 bg-amber-100 text-amber-800 rounded">
                                System
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 line-clamp-1 mt-0.5">
                            {role.description || 'Configured system role.'}
                          </p>
                        </div>
                      </div>
                      <Badge status={role.status} size="sm" />
                    </div>

                    <div className="mt-3 flex items-center justify-between text-xs text-slate-400 border-t border-slate-200/60 pt-2">
                      <span className="flex items-center gap-1 font-medium text-slate-600">
                        <Users className="w-3.5 h-3.5 text-slate-400" />
                        <span>{role.user_count || 0} user(s) assigned</span>
                      </span>

                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        {can('roles.create') && !isSystem && (
                          <button
                            type="button"
                            onClick={() => handleOpenClone(role)}
                            title="Clone role"
                            className="p-1 text-slate-400 hover:text-slate-700 rounded hover:bg-slate-200/50"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {can('roles.edit') && !isSystem && (
                          <button
                            type="button"
                            onClick={() => handleOpenEdit(role)}
                            title="Edit role name & description"
                            className="p-1 text-indigo-600 hover:text-indigo-800 rounded hover:bg-indigo-100/50"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {can('roles.delete') && !isSystem && (
                          <button
                            type="button"
                            onClick={() => setConfirmDelete({ open: true, role, loading: false })}
                            title="Delete role"
                            className="p-1 text-rose-500 hover:text-rose-700 rounded hover:bg-rose-100/50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Main Column: Workspace (8 Cols) */}
        <div className="lg:col-span-8 bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
          {selectedRole ? (
            <>
              {/* Role Header Banner */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 gap-4">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-bold text-slate-900">{selectedRole.name}</h3>
                    {isSuperAdminRole ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200 px-2.5 py-0.5 rounded-full">
                        <Lock className="w-3 h-3 text-amber-600" /> Protected System Role (Full Access)
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold bg-slate-100 text-slate-700 px-2.5 py-0.5 rounded-full">
                        Custom Role
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {selectedRole.description || 'Fine-tune pricing tier confidentiality and module access.'}
                  </p>
                </div>

                {/* Header Action Buttons */}
                <div className="flex items-center gap-2 flex-wrap">
                  {!isSuperAdminRole && can('roles.edit') && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPresetModalOpen(true)}
                      icon={<Sparkles className="w-3.5 h-3.5 text-indigo-600" />}
                    >
                      Apply Template Preset
                    </Button>
                  )}

                  {!isSuperAdminRole && can('roles.edit') && (
                    <Button
                      onClick={handleSavePermissions}
                      loading={savingPerms}
                      disabled={!hasUnsavedChanges}
                      icon={<Save className="w-4 h-4" />}
                      className={hasUnsavedChanges ? 'ring-2 ring-indigo-500 ring-offset-1 animate-pulse' : ''}
                    >
                      {hasUnsavedChanges ? 'Save Changes *' : 'Saved'}
                    </Button>
                  )}
                </div>
              </div>

              {/* Live Security & Access Overview Card */}
              <div className="p-4 rounded-xl border border-slate-200 bg-slate-50/70 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-indigo-600" />
                    Live Security Profile & Visibility Summary
                  </span>
                  {hasUnsavedChanges && (
                    <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      Unsaved changes pending
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  {/* Cost Confidentiality Status */}
                  <div className={`p-3 rounded-lg border ${
                    isCostViewAllowed
                      ? 'bg-amber-50/80 border-amber-200 text-amber-900'
                      : 'bg-emerald-50/80 border-emerald-200 text-emerald-900'
                  }`}>
                    <div className="flex items-center gap-1.5 font-bold mb-1">
                      {isCostViewAllowed ? (
                        <>
                          <AlertCircle className="w-4 h-4 text-amber-600" />
                          <span>Factory Cost: Exposed</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span>Factory Cost: Confidential 🔒</span>
                        </>
                      )}
                    </div>
                    <p className="text-[11px] opacity-80">
                      {isCostViewAllowed
                        ? 'Internal factory base unit costs & markup are visible to this role.'
                        : 'Factory base cost and net cost margins are completely hidden.'}
                    </p>
                  </div>

                  {/* Selling Tiers Active */}
                  <div className="p-3 rounded-lg border bg-white border-slate-200 text-slate-800">
                    <span className="font-bold block mb-1">Selling Tiers Visible:</span>
                    <div className="flex flex-wrap gap-1">
                      {PRICE_TIERS.filter(t => !t.isConfidential).map(tier => {
                        const isVisible = isSuperAdminRole || (permByKey[tier.viewKey] && selectedPermIds.has(permByKey[tier.viewKey]!.id));
                        return (
                          <span
                            key={tier.id}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                              isVisible
                                ? 'bg-indigo-100 text-indigo-800'
                                : 'bg-slate-100 text-slate-400 line-through'
                            }`}
                          >
                            {tier.shortName}
                          </span>
                        );
                      })}
                    </div>
                  </div>

                  {/* Active Users Summary */}
                  <div className="p-3 rounded-lg border bg-white border-slate-200 text-slate-800 flex flex-col justify-between">
                    <div>
                      <span className="font-bold block mb-1">Assigned Personnel:</span>
                      <p className="text-[11px] text-slate-600">
                        {roleMembers.length === 0
                          ? 'No employees assigned yet.'
                          : `${roleMembers.length} employee(s) actively assigned.`}
                      </p>
                    </div>
                    {can('users.edit') && (
                      <button
                        type="button"
                        onClick={() => setAssignModalOpen(true)}
                        className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 mt-2 flex items-center gap-1 self-start"
                      >
                        <UserPlus className="w-3 h-3" />
                        <span>Assign Employee...</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* System Role Lock Warning */}
              {isSuperAdminRole && (
                <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p>
                    The <strong>Super Admin</strong> is a protected system role with full, immutable privileges across all platform modules and pricing tiers. These permissions cannot be restricted.
                  </p>
                </div>
              )}

              {/* Navigation Tabs */}
              <div className="flex items-center gap-2 border-b border-slate-200">
                <button
                  type="button"
                  onClick={() => setActiveTab('pricing')}
                  className={`pb-2.5 px-3 text-xs font-bold transition flex items-center gap-2 border-b-2 ${
                    activeTab === 'pricing'
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span>1. Pricing Tier Security Matrix</span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('modules')}
                  className={`pb-2.5 px-3 text-xs font-bold transition flex items-center gap-2 border-b-2 ${
                    activeTab === 'modules'
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <Package className="w-3.5 h-3.5" />
                  <span>2. Application Modules Access</span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('members')}
                  className={`pb-2.5 px-3 text-xs font-bold transition flex items-center gap-2 border-b-2 ${
                    activeTab === 'members'
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-slate-500 hover:text-slate-800'
                  }`}
                >
                  <Users className="w-3.5 h-3.5" />
                  <span>3. Assigned Team Members ({roleMembers.length})</span>
                </button>
              </div>

              {/* Tab 1: Pricing Tier Security Matrix */}
              {activeTab === 'pricing' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 flex items-center gap-1.5">
                        <span>Commercial Price Tier Visibility & Editing Matrix</span>
                      </h4>
                      <p className="text-xs text-slate-500">
                        Control exactly which pricing tiers employees with this role can inspect (👁️) or modify (✏️).
                      </p>
                    </div>
                  </div>

                  {loadingPerms ? (
                    <TableSkeleton rows={5} columns={3} />
                  ) : (
                    <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden bg-white">
                      {PRICE_TIERS.map(tier => {
                        const viewPerm = permByKey[tier.viewKey];
                        const editPerm = permByKey[tier.editKey];

                        const isViewAllowed = isSuperAdminRole || Boolean(viewPerm && selectedPermIds.has(viewPerm.id));
                        const isEditAllowed = isSuperAdminRole || Boolean(editPerm && selectedPermIds.has(editPerm.id));

                        return (
                          <div
                            key={tier.id}
                            className={`p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition ${
                              tier.isConfidential && isViewAllowed
                                ? 'bg-amber-50/40'
                                : tier.isConfidential
                                ? 'bg-emerald-50/20'
                                : 'hover:bg-slate-50/60'
                            }`}
                          >
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-bold text-slate-900">{tier.title}</span>
                                {tier.isConfidential && (
                                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-rose-100 text-rose-800 border border-rose-200 flex items-center gap-1">
                                    <Lock className="w-3 h-3" /> Confidential Cost Data
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500">{tier.description}</p>
                            </div>

                            {/* View & Edit Toggle Buttons */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {/* View Toggle */}
                              <button
                                type="button"
                                disabled={isSuperAdminRole}
                                onClick={() => viewPerm && togglePermissionByKey(tier.viewKey)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 border ${
                                  isViewAllowed
                                    ? 'bg-indigo-50 border-indigo-300 text-indigo-700 hover:bg-indigo-100'
                                    : 'bg-slate-100 border-slate-200 text-slate-400 hover:bg-slate-200'
                                } disabled:opacity-75 disabled:cursor-not-allowed`}
                              >
                                {isViewAllowed ? (
                                  <>
                                    <Eye className="w-3.5 h-3.5 text-indigo-600" />
                                    <span>Visible</span>
                                  </>
                                ) : (
                                  <>
                                    <EyeOff className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Hidden 🔒</span>
                                  </>
                                )}
                              </button>

                              {/* Edit Toggle */}
                              <button
                                type="button"
                                disabled={isSuperAdminRole || !isViewAllowed}
                                onClick={() => editPerm && togglePermissionByKey(tier.editKey)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 border ${
                                  isEditAllowed
                                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                                    : 'bg-slate-100 border-slate-200 text-slate-400 hover:bg-slate-200'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                              >
                                {isEditAllowed ? (
                                  <>
                                    <Pencil className="w-3.5 h-3.5 text-emerald-600" />
                                    <span>Can Edit</span>
                                  </>
                                ) : (
                                  <>
                                    <Lock className="w-3.5 h-3.5 text-slate-400" />
                                    <span>Read Only</span>
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Tab 2: Application Modules Access */}
              {activeTab === 'modules' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-slate-900">Application Module Access Privileges</h4>
                      <p className="text-xs text-slate-500">
                        Choose high-level access tiers (No Access, View Only, Full Manage) or customize granular permissions.
                      </p>
                    </div>
                  </div>

                  {loadingPerms ? (
                    <TableSkeleton rows={6} columns={3} />
                  ) : (
                    <div className="space-y-3">
                      {MODULE_DEFS.map(mod => {
                        const Icon = mod.icon;
                        const level = getModuleAccessLevel(mod);
                        const isExpanded = Boolean(expandedModules[mod.id]);

                        return (
                          <div
                            key={mod.id}
                            className="rounded-xl border border-slate-200 bg-white overflow-hidden transition-all"
                          >
                            <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                              <div className="flex items-start gap-3">
                                <div className={`p-2 rounded-lg mt-0.5 ${
                                  level === 'none'
                                    ? 'bg-slate-100 text-slate-400'
                                    : level === 'view'
                                    ? 'bg-sky-50 text-sky-600'
                                    : 'bg-indigo-50 text-indigo-600'
                                }`}>
                                  <Icon className="w-4 h-4" />
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-slate-900">{mod.name}</span>
                                    {level === 'custom' && (
                                      <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-amber-100 text-amber-800">
                                        Custom Mix
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-slate-500 mt-0.5">{mod.description}</p>
                                </div>
                              </div>

                              {/* 3-State Level Selector Pills */}
                              <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg self-start sm:self-center">
                                <button
                                  type="button"
                                  disabled={isSuperAdminRole}
                                  onClick={() => handleSetModuleLevel(mod, 'none')}
                                  className={`px-2.5 py-1 rounded text-xs font-bold transition ${
                                    level === 'none'
                                      ? 'bg-white text-slate-900 shadow-xs'
                                      : 'text-slate-500 hover:text-slate-900'
                                  } disabled:opacity-50`}
                                >
                                  No Access
                                </button>
                                <button
                                  type="button"
                                  disabled={isSuperAdminRole}
                                  onClick={() => handleSetModuleLevel(mod, 'view')}
                                  className={`px-2.5 py-1 rounded text-xs font-bold transition ${
                                    level === 'view'
                                      ? 'bg-white text-indigo-700 shadow-xs'
                                      : 'text-slate-500 hover:text-slate-900'
                                  } disabled:opacity-50`}
                                >
                                  View Only
                                </button>
                                <button
                                  type="button"
                                  disabled={isSuperAdminRole}
                                  onClick={() => handleSetModuleLevel(mod, 'full')}
                                  className={`px-2.5 py-1 rounded text-xs font-bold transition ${
                                    level === 'full'
                                      ? 'bg-indigo-600 text-white shadow-xs'
                                      : 'text-slate-500 hover:text-slate-900'
                                  } disabled:opacity-50`}
                                >
                                  Full Control
                                </button>
                              </div>
                            </div>

                            {/* Fine-Grained Expand Toggle */}
                            <div className="px-4 py-2 bg-slate-50/60 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                              <button
                                type="button"
                                onClick={() => setExpandedModules(prev => ({ ...prev, [mod.id]: !prev[mod.id] }))}
                                className="font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                              >
                                {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                <span>Fine-grained permissions ({mod.actions.filter(a => permByKey[a.key] && selectedPermIds.has(permByKey[a.key]!.id)).length}/{mod.actions.length} enabled)</span>
                              </button>
                            </div>

                            {/* Expanded Fine-Grained Actions */}
                            {isExpanded && (
                              <div className="p-4 bg-slate-50/40 border-t border-slate-100 divide-y divide-slate-100">
                                {mod.actions.map(action => {
                                  const perm = permByKey[action.key];
                                  const isChecked = isSuperAdminRole || Boolean(perm && selectedPermIds.has(perm.id));

                                  return (
                                    <label
                                      key={action.key}
                                      className={`py-2.5 flex items-start justify-between cursor-pointer select-none ${
                                        isSuperAdminRole ? 'cursor-default' : 'hover:bg-slate-100/50'
                                      }`}
                                    >
                                      <div>
                                        <span className="text-xs font-bold text-slate-800 block">{action.label}</span>
                                        <span className="text-[11px] text-slate-500">{action.desc}</span>
                                      </div>
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        disabled={isSuperAdminRole}
                                        onChange={() => perm && togglePermissionByKey(action.key)}
                                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 mt-0.5 disabled:opacity-50"
                                      />
                                    </label>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Tab 3: Assigned Members */}
              {activeTab === 'members' && (
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div>
                      <h4 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                        <span>Employees Assigned to "{selectedRole.name}"</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">
                          {roleMembers.length} user(s)
                        </span>
                      </h4>
                      <p className="text-xs text-slate-500">
                        View active team members holding this role, or assign additional users directly.
                      </p>
                    </div>

                    {can('users.edit') && (
                      <Button
                        size="sm"
                        onClick={() => setAssignModalOpen(true)}
                        icon={<UserPlus className="w-3.5 h-3.5" />}
                      >
                        Assign Employee
                      </Button>
                    )}
                  </div>

                  {roleMembers.length === 0 ? (
                    <div className="p-8 text-center border border-dashed border-slate-300 rounded-xl bg-slate-50/50">
                      <Users className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-xs font-semibold text-slate-700">No employees are currently assigned to this role.</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">Click "Assign Employee" to map a staff member to this role.</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden bg-white">
                      {roleMembers.map(user => (
                        <div key={user.id} className="p-3.5 flex items-center justify-between gap-3 hover:bg-slate-50/60 transition">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-xs">
                              {user.full_name?.charAt(0) || 'U'}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-slate-900">{user.full_name}</span>
                                <Badge status={user.status} size="sm" />
                              </div>
                              <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                                <span>{user.email}</span>
                                {user.department_name && (
                                  <>
                                    <span>•</span>
                                    <span>{user.department_name}</span>
                                  </>
                                )}
                                {user.job_title && (
                                  <>
                                    <span>•</span>
                                    <span>{user.job_title}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="text-[11px] text-slate-400">
                            {user.employee_id ? `#${user.employee_id}` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="py-20 text-center text-slate-400 space-y-2">
              <ShieldCheck className="w-12 h-12 mx-auto text-slate-300" />
              <p className="text-sm font-medium text-slate-600">Select a role from the list on the left to configure.</p>
            </div>
          )}
        </div>
      </div>

      {/* Preset Picker Modal */}
      <Modal
        isOpen={presetModalOpen}
        onClose={() => setPresetModalOpen(false)}
        title="Apply Business Role Template Preset"
        description="Select a standard Geobit departmental role preset to instantly configure appropriate permissions."
        maxWidth="xl"
      >
        <div className="space-y-3">
          {BUSINESS_PRESETS.map(preset => (
            <div
              key={preset.id}
              onClick={() => handleApplyPreset(preset)}
              className="p-3.5 rounded-xl border border-slate-200 hover:border-indigo-500 hover:bg-indigo-50/30 cursor-pointer transition flex items-start justify-between gap-4"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-900">{preset.name}</span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${preset.badgeColor}`}>
                    {preset.badge}
                  </span>
                </div>
                <p className="text-xs text-slate-600">{preset.description}</p>
              </div>

              <Button size="sm" variant="outline" type="button">
                Apply
              </Button>
            </div>
          ))}

          <div className="flex justify-end pt-3 border-t border-slate-100">
            <Button variant="outline" onClick={() => setPresetModalOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Role Create / Edit Modal */}
      <Modal
        isOpen={roleModalOpen}
        onClose={() => setRoleModalOpen(false)}
        title={editingRole ? 'Edit Role Details' : cloneFromId ? 'Clone Role Template' : 'Create New Role'}
        description="Define organizational roles and configure starting permission templates."
        maxWidth="md"
      >
        <form onSubmit={handleSaveRole} className="space-y-4">
          <Input
            label="Role Name"
            required
            value={roleName}
            onChange={e => setRoleName(e.target.value)}
            placeholder="e.g. Senior Estimation Engineer, Procurement Specialist"
          />

          <div className="w-full">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1">
              Description
            </label>
            <textarea
              value={roleDesc}
              onChange={e => setRoleDesc(e.target.value)}
              placeholder="Brief description of this role's responsibilities and permissions..."
              rows={3}
              className="w-full rounded-lg border border-slate-300 text-xs p-2.5 focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600"
            />
          </div>

          {!editingRole && !cloneFromId && (
            <div className="w-full">
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1">
                Starting Permission Template
              </label>
              <select
                value={rolePresetChoice}
                onChange={e => setRolePresetChoice(e.target.value)}
                className="w-full rounded-lg border border-slate-300 text-xs p-2 bg-white focus:ring-indigo-600"
              >
                <option value="custom">Start Empty / Blank (Configure Manually)</option>
                {BUSINESS_PRESETS.map(p => (
                  <option key={p.id} value={p.id}>
                    Preset: {p.name} ({p.badge})
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-slate-500 mt-1">
                Pre-configures recommended pricing tier visibility and module access.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
            <Button variant="outline" type="button" onClick={() => setRoleModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={roleLoading}>
              {editingRole ? 'Save Changes' : 'Create Role'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Assign User to Role Modal */}
      <Modal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        title={`Assign Employee to "${selectedRole?.name}"`}
        description="Select an employee from the directory to assign them to this role."
        maxWidth="md"
      >
        <form onSubmit={handleAssignUser} className="space-y-4">
          <div className="w-full">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1">
              Select Employee
            </label>
            <select
              required
              value={selectedUserToAssign}
              onChange={e => setSelectedUserToAssign(e.target.value)}
              className="w-full rounded-lg border border-slate-300 text-xs p-2.5 bg-white focus:ring-indigo-600"
            >
              <option value="">-- Choose employee --</option>
              {allUsers.map(u => (
                <option key={u.id} value={u.id}>
                  {u.full_name} ({u.email}) {u.role_name ? `— Currently: ${u.role_name}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
            <Button variant="outline" type="button" onClick={() => setAssignModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={assigningUser} disabled={!selectedUserToAssign}>
              Assign to Role
            </Button>
          </div>
        </form>
      </Modal>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, role: null, loading: false })}
        onConfirm={handleDeleteRole}
        title="Delete Custom Role"
        message={`Are you sure you want to delete the role "${confirmDelete.role?.name}"? This action is permanent. Any users assigned to this role must be reassigned first.`}
        confirmLabel="Delete Role"
        variant="danger"
        loading={confirmDelete.loading}
      />
    </div>
  );
};
